/* ============================================================
   Контент-стор новой витрины (zustand + persist + бэкенд).

   Новости, партнёры, проекты, победители и настройки федерации
   редактируются в админке и сразу видны на публичных страницах.

   Источник правды — бэкенд (GET /api/content), localStorage —
   офлайн-кэш, сиды из дизайн-макета (data.js) — фолбэк, пока
   на сервере нет данных. Каждое изменение в админке публикуется
   на сервер (PUT /api/content/<key>, только админ).

   Спонсорские заявки живут на бэкенде (/api/sponsors) и в
   localStorage не сохраняются. Заявки на участие в турнирах
   (бэкенд-сущности) ходят через useAppStore.
   ============================================================ */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAppStore, apiFetch } from '../store';
import { NEWS, PARTNERS, PROJECTS, WINNERS, TEAM, DOCS, TOURNAMENTS, MLBB_TOURS, COUNTERS, LIVE_DEFAULTS } from './data';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5252/api';

// Публичные картинки контента (логотипы партнёров и т.п.) отдаёт бэкенд
// без /api — 'uploads/xxx.png' → `${FILE_BASE}/uploads/xxx.png`.
const FILE_BASE = API_URL.replace(/\/api$/, '');
export const uploadUrl = (path) => (path ? `${FILE_BASE}/${String(path).replace(/^\/+/, '')}` : null);

const LEGACY_ABOUT = 'Федерация киберспорта Северо-Казахстанской области. Развиваем киберспорт в регионе: турниры, лиги, обучение и трансляции.';
export const federationAbout = (settings, lang, fallback) => {
  const localized = settings[`about_${lang}`];
  if (localized) return localized;
  return lang === 'ru' && settings.about && settings.about !== LEGACY_ABOUT ? settings.about : fallback;
};

const uid = () => Math.random().toString(36).slice(2, 9);

/** Пост показывается на витрине, если не отмечен черновиком. Записи, созданные
    до появления флага, поля не имеют — считаем их опубликованными.
    Черновик прячется из списков, но лежит в общем документе контента:
    прямая ссылка на него откроется — это заготовка, а не секрет. */
export const isPublished = (n) => !!n && n.published !== false;

// Ключи, которые синхронизируются с бэкендом (FedContent.ALLOWED_KEYS)
const CONTENT_KEYS = ['news', 'partners', 'projects', 'winners', 'team', 'docs', 'gallery', 'tournaments', 'settings', 'counters', 'live'];

// Простые CRUD-списки (партнёры / проекты / победители / команда).
// Каждый элемент получает стабильный id — по нему админка редактирует,
// удаляет и переставляет записи, а публичные страницы строят React key.
const LIST_KEYS = ['partners', 'projects', 'winners', 'team', 'docs', 'gallery'];

// Проставить недостающие id (сиды из data.js и старые записи из кэша/бэкенда
// приходят без id). Существующие id сохраняются, чтобы правки не «прыгали».
const withIds = (list) =>
  Array.isArray(list)
    ? list.map((x) => (x && typeof x === 'object' && x.id ? x : { ...x, id: 'i' + uid() }))
    : list;

const normalizeLists = (obj) => {
  const patch = {};
  for (const k of LIST_KEYS) if (obj && obj[k] !== undefined) patch[k] = withIds(obj[k]);
  return patch;
};

const TYPE_LABELS = { gen: 'Общее', tour: 'Турнир', media: 'Медиа' };

// Турниры MLBB заданы в data.js: их id зашиты в механику живой сетки
// (fed/mlbb/store.js) и в бэкенд, поэтому создать или удалить такой турнир
// из админки нельзя — но редактировать можно, и правки обязаны переживать
// перезагрузку. Раньше сид безусловно вставлялся первым и вытеснял копию с
// сервера, из-за чего любое изменение MLBB-турнира откатывалось.
export const MLBB_IDS = MLBB_TOURS.map((t) => t.id);
export const isSeededTour = (id) => MLBB_IDS.includes(id);

/** Добавить MLBB-турниры, которых в сохранённом списке ещё нет. Существующие
    записи не трогаем — они уже отредактированы в админке. */
const withMlbb = (list) => {
  if (!Array.isArray(list)) return [...MLBB_TOURS];
  const known = new Set(list.map((t) => t && t.id));
  return [...MLBB_TOURS.filter((t) => !known.has(t.id)), ...list];
};

// Поля, которые админка не заполняет (KZ-переводы, итоги квалификаций,
// картинка турнира), берём из сидов по id — но только те, которых в
// сохранённой записи нет вовсе. Всё, что пришло с сервера, в приоритете:
// пустая строка — это осознанно очищенное поле, а не «нет значения».
const SEEDS_BY_ID = new Map([...TOURNAMENTS, ...MLBB_TOURS].map((t) => [t.id, t]));
const withSeedFields = (list) =>
  (Array.isArray(list) ? list : []).map((t) => {
    const seed = t && SEEDS_BY_ID.get(t.id);
    if (!seed) return t;
    const patch = {};
    for (const k of Object.keys(seed)) {
      if (t[k] === undefined) patch[k] = seed[k];
    }
    return Object.keys(patch).length ? { ...t, ...patch } : t;
  });

const normalizeTours = (list) => withMlbb(withSeedFields(list));

const authHeaders = () => {
  const h = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('access_token');
    if (token) h.Authorization = `Bearer ${token}`;
  } catch (e) {}
  return h;
};

const fireToast = (msg, type) => {
  try { useAppStore.getState().fire(msg, type); } catch (e) {}
};

const responseError = async (res, fallback) => {
  const data = await res.json().catch(() => ({}));
  return data.error || data.message || `${fallback} (${res.status})`;
};

export const useFedContent = create(
  persist(
    (set, get) => ({
      news: NEWS,
      partners: withIds(PARTNERS),
      projects: withIds(PROJECTS),
      winners: withIds(WINNERS),
      team: withIds(TEAM),
      docs: DOCS,
      gallery: [],
      tournaments: TOURNAMENTS,
      counters: COUNTERS,
      live: LIVE_DEFAULTS,
      sponsorRequests: [],
      settings: {
        name: 'Федерация киберспорта СКО',
        region: 'Северо-Казахстанская область',
        phone: '+7 700 678 0221',
        telegram: '@fc_sko',
        email: 'admin@sko-esports.kz',
        city: 'Петропавловск',
        about: '',
      },

      // ===== синхронизация с бэкендом =====

      /** Подтянуть контент с сервера (публично). Ключи без записей на
          сервере остаются локальными (сиды/кэш). */
      hydrate: async () => {
        try {
          const res = await apiFetch(`${API_URL}/content`);
          if (!res.ok) return;
          const data = await res.json();
          const patch = {};
          for (const key of CONTENT_KEYS) {
            if (data.content && data.content[key] !== undefined) {
              let value = (key === 'settings' || key === 'live')
                ? { ...get()[key], ...data.content[key] }
                : data.content[key];
              if (LIST_KEYS.includes(key)) value = withIds(value);
              patch[key] = value;
            }
          }
          if (patch.tournaments) patch.tournaments = normalizeTours(patch.tournaments);
          if (Object.keys(patch).length) set(patch);
        } catch (e) { /* бэкенд недоступен — работаем на локальных данных */ }
      },

      /** Опубликовать ВЕСЬ текущий контент на сервер (только админ).
          Нужно один раз после деплоя, чтобы наполнить пустую БД сидами. */
      pushAll: async () => {
        const results = await Promise.all(CONTENT_KEYS.map((k) => get().pushKey(k)));
        return results.every(Boolean);
      },

      /** Опубликовать текущее значение ключа на сервер (только админ).
          Возвращает true/false — вызывающий код показывает «сохранено»
          ТОЛЬКО по true, иначе админ верит тосту, а правка живёт лишь
          в localStorage и пропадает на следующем hydrate. */
      pushKey: async (key, { silent = false } = {}) => {
        try {
          const res = await apiFetch(`${API_URL}/content/${key}`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({ value: get()[key] }),
          });
          if (!res.ok) throw new Error(String(res.status));
          return true;
        } catch (e) {
          if (!silent) fireToast('Не сохранено на сервере — изменение осталось только в этом браузере', 'error');
          return false;
        }
      },

      /** Загрузить картинку контента (логотип и т.п.), только админ.
          Возвращает путь ('uploads/content_....png') или бросает исключение. */
      uploadImage: async (file) => {
        const form = new FormData();
        form.append('image', file);
        const headers = authHeaders();
        delete headers['Content-Type']; // multipart сам проставит boundary
        const res = await apiFetch(`${API_URL}/content/upload-image`, {
          method: 'POST', headers, body: form,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Не удалось загрузить файл');
        return data.path;
      },

      uploadGalleryImage: async (file) => {
        const form = new FormData();
        form.append('image', file);
        const headers = authHeaders();
        delete headers['Content-Type'];
        const res = await apiFetch(`${API_URL}/content/upload-gallery`, { method: 'POST', headers, body: form });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Не удалось загрузить фотографию');
        return data.path;
      },

      /** Загрузить документ витрины (регламент, PDF), только админ.
          Возвращает { path, size } или бросает исключение. */
      uploadDoc: async (file) => {
        const form = new FormData();
        form.append('file', file);
        const headers = authHeaders();
        delete headers['Content-Type'];
        const res = await apiFetch(`${API_URL}/content/upload-doc`, {
          method: 'POST', headers, body: form,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Не удалось загрузить файл');
        return data;
      },

      // ===== документы (регламенты) =====
      saveDoc: (item) => {
        set((s) => {
          const list = s.docs || [];
          const exists = item.id && list.some((d) => d.id === item.id);
          if (exists) return { docs: list.map((d) => (d.id === item.id ? { ...d, ...item } : d)) };
          return { docs: [...list, { ...item, id: item.id || 'd' + uid() }] };
        });
        return get().pushKey('docs');
      },
      deleteDoc: (id) => {
        set((s) => ({ docs: (s.docs || []).filter((d) => d.id !== id) }));
        return get().pushKey('docs');
      },
      moveDoc: (id, dir) => {
        set((s) => {
          const list = [...(s.docs || [])];
          const i = list.findIndex((d) => d.id === id);
          const j = i + dir;
          if (i < 0 || j < 0 || j >= list.length) return {};
          [list[i], list[j]] = [list[j], list[i]];
          return { docs: list };
        });
        return get().pushKey('docs');
      },

      // ===== новости =====
      // Все мутации возвращают промис с результатом публикации: админка
      // рисует «сохранено» только когда сервер действительно принял.
      saveNews: (item) => {
        set((s) => {
          const exists = item.id && s.news.some((n) => n.id === item.id);
          if (exists) return { news: s.news.map((n) => (n.id === item.id ? { ...n, ...item } : n)) };
          return { news: [{ ...item, id: item.id || 'n' + uid() }, ...s.news] };
        });
        return get().pushKey('news');
      },
      deleteNews: (id) => {
        set((s) => ({ news: s.news.filter((n) => n.id !== id) }));
        return get().pushKey('news');
      },

      // ===== турниры (локальный контент витрины) =====
      saveTournament: (item) => {
        set((s) => {
          const exists = item.id && s.tournaments.some((t) => t.id === item.id);
          if (exists) return { tournaments: s.tournaments.map((t) => (t.id === item.id ? { ...t, ...item } : t)) };
          return { tournaments: [...s.tournaments, { hue: 8, hasData: false, ...item, id: item.id || 't' + uid() }] };
        });
        return get().pushKey('tournaments');
      },
      deleteTournament: (id) => {
        // MLBB-турнир вернётся из сидов на следующей загрузке — честнее
        // сказать это сразу, чем показать «удалено» и воскресить запись
        if (isSeededTour(id)) {
          fireToast('Турнир с живой сеткой MLBB задан в коде витрины — удалить его из админки нельзя', 'error');
          return Promise.resolve(false);
        }
        set((s) => ({ tournaments: s.tournaments.filter((t) => t.id !== id) }));
        return get().pushKey('tournaments');
      },

      // ===== простые списки (партнёры / проекты / победители / команда) =====

      /** Добавить новую запись или обновить существующую по id. */
      saveListItem: (key, item) => {
        set((s) => {
          const list = s[key] || [];
          const exists = item.id && list.some((x) => x.id === item.id);
          if (exists) return { [key]: list.map((x) => (x.id === item.id ? { ...x, ...item } : x)) };
          return { [key]: [...list, { ...item, id: item.id || 'i' + uid() }] };
        });
        return get().pushKey(key);
      },
      deleteListItem: (key, id) => {
        set((s) => ({ [key]: (s[key] || []).filter((x) => x.id !== id) }));
        return get().pushKey(key);
      },
      /** Переставить запись вверх/вниз (dir = -1 | +1). Порядок в массиве —
          это порядок на витрине (карточки, подиум победителей). */
      moveListItem: (key, id, dir) => {
        set((s) => {
          const list = [...(s[key] || [])];
          const i = list.findIndex((x) => x.id === id);
          const j = i + dir;
          if (i < 0 || j < 0 || j >= list.length) return {};
          [list[i], list[j]] = [list[j], list[i]];
          return { [key]: list };
        });
        return get().pushKey(key);
      },

      addGalleryItems: (items) => {
        set((s) => ({ gallery: [...items.map((item) => ({ ...item, id: item.id || 'g' + uid() })), ...(s.gallery || [])] }));
        return get().pushKey('gallery');
      },
      deleteGalleryItem: (id) => {
        set((s) => ({ gallery: (s.gallery || []).filter((item) => item.id !== id) }));
        return get().pushKey('gallery');
      },

      // ===== заявки спонсоров (бэкенд) =====

      /** Список заявок с сервера (только админ). */
      fetchSponsorRequests: async () => {
        try {
          const res = await apiFetch(`${API_URL}/sponsors`, { headers: authHeaders() });
          if (!res.ok) throw new Error(await responseError(res, 'Не удалось загрузить заявки'));
          const data = await res.json();
          if (Array.isArray(data.requests)) set({ sponsorRequests: data.requests });
        } catch (e) {
          fireToast(e.message || 'Не удалось загрузить спонсорские заявки', 'error');
        }
      },

      /** Публичная отправка заявки из модалки. Бросает исключение при неудаче. */
      createSponsorRequest: async ({ name, phone, type, message }) => {
        const res = await apiFetch(`${API_URL}/sponsors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, phone, type, message }),
        });
        if (!res.ok) throw new Error(await responseError(res, 'Не удалось отправить заявку'));
        const data = await res.json();
        set((s) => ({ sponsorRequests: [data.request, ...s.sponsorRequests] }));
        return true;
      },

      /** Локальная запись — фолбэк, когда бэкенд недоступен. */
      addSponsorRequest: (r) =>
        set((s) => ({
          sponsorRequests: [
            { id: 'local-' + uid(), status: 'new', date: new Date().toLocaleDateString('ru-RU'), typeLabel: TYPE_LABELS[r.type] || r.type, ...r },
            ...s.sponsorRequests,
          ],
        })),

      // Заявки живут ТОЛЬКО на сервере (в localStorage их не пишем), поэтому
      // при неудаче откатываем список: иначе в таблице «В работе», на сервере
      // по-прежнему «Новая», и после перезагрузки всё возвращается назад.
      setSponsorStatus: async (id, status) => {
        const prev = get().sponsorRequests;
        set((s) => ({ sponsorRequests: s.sponsorRequests.map((r) => (r.id === id ? { ...r, status } : r)) }));
        if (typeof id !== 'number') return true;   // локальный фолбэк-элемент
        try {
          const res = await apiFetch(`${API_URL}/sponsors/${id}/status`, {
            method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status }),
          });
          if (!res.ok) throw new Error(await responseError(res, 'Не удалось изменить статус'));
          return true;
        } catch (e) {
          set({ sponsorRequests: prev });
          fireToast(e.message || 'Не удалось изменить статус заявки', 'error');
          return false;
        }
      },
      deleteSponsorRequest: async (id) => {
        const prev = get().sponsorRequests;
        set((s) => ({ sponsorRequests: s.sponsorRequests.filter((r) => r.id !== id) }));
        if (typeof id !== 'number') return true;
        try {
          const res = await apiFetch(`${API_URL}/sponsors/${id}`, { method: 'DELETE', headers: authHeaders() });
          if (!res.ok) throw new Error(await responseError(res, 'Не удалось удалить заявку'));
          fireToast('Заявка удалена', 'ok');
          return true;
        } catch (e) {
          set({ sponsorRequests: prev });
          fireToast(e.message || 'Не удалось удалить заявку', 'error');
          return false;
        }
      },

      // ===== настройки =====
      saveSettings: (settings) => {
        set((s) => ({ settings: { ...s.settings, ...settings } }));
        return get().pushKey('settings');
      },

      // ===== страница «Эфир» =====
      saveLive: (live) => {
        set((s) => ({ live: { ...s.live, ...live } }));
        return get().pushKey('live');
      },

      // ===== счётчики на главной =====
      saveCounters: (counters) => {
        set({ counters });
        return get().pushKey('counters');
      },

      /** Сброс к сидам из макета (+публикация на сервер) */
      resetContent: () => {
        set({ news: NEWS, partners: withIds(PARTNERS), projects: withIds(PROJECTS), winners: withIds(WINNERS), team: withIds(TEAM), docs: DOCS, tournaments: TOURNAMENTS, counters: COUNTERS, live: LIVE_DEFAULTS });
        CONTENT_KEYS.filter((k) => k !== 'settings').forEach((k) => get().pushKey(k));
      },
    }),
    {
      name: 'fed-content-v1',
      version: 3,
      // спонсорские заявки живут на сервере — в localStorage не пишем
      partialize: (s) => Object.fromEntries(CONTENT_KEYS.map((k) => [k, s[k]])),
      // как в дефолтном merge (поверхностный), плюс добор KZ-переводов:
      // в кэше лежат турниры, сохранённые до появления *_kz полей
      merge: (persisted, current) => {
        const merged = { ...current, ...persisted };
        if (merged.tournaments) merged.tournaments = normalizeTours(merged.tournaments);
        Object.assign(merged, normalizeLists(merged));
        return merged;
      },
      migrate: (persisted, version) => {
        if (persisted && typeof persisted === 'object') {
          delete persisted.sponsorRequests;
          // v3: мок-сиды «Человек 1–6» и фейковые PDF убраны — сбрасываем
          // закэшированные, чтобы старые браузеры не показывали заглушки
          if (version < 3) {
            delete persisted.team;
            delete persisted.docs;
          }
        }
        return persisted;
      },
    }
  )
);
